import api from "./index";

export interface CreateUpdateTreatmentPayload {
  id?: string;
  patientId: string;
  doctorId: string;
  clinicId: string;
  medicalConditionId: string;
  chiefComplaint: string;
  overjet: string;
  diagnosis: string;
  other_diagnosis: string;
  allergyId: string;
  habit: string;
  other_habit: string;
  facialProfile: string;
  nasolabialAngle: string;
  crossbite: string;
  lipPosture: string;
  noseSize: string;
  treatmentType: string;
  lipProtrusion: string;
  vto: string;
  tonsils: string;
  facialForm: string;
  mentolabialSulcus: string;
  frenum: string;
  oralHygieneStatus: string;
  chin: string;
  facialSymmetry: string;
  facialDivergence: string;
  tongueSize: string;
  treatmentObjective: string;
  jawType?: string;
  dentalAndIntraOralExamination?: {
    upperJawOptions?: number[];
    lowerJawOptions?: number[];
    missingTeeth?: number[];
    extractedTeeth?: number[];
  };
}

// 👇 add photos as File[] when needed
export const createOrUpdateTreatment = async (
  payload: CreateUpdateTreatmentPayload,
  preTreatmentPhotos: File[] = []
) => {
  const formData = new FormData();

  // 👇 IMPORTANT: stringify payload
  formData.append("data", JSON.stringify(payload));

  // 👇 add multiple photos if any
  preTreatmentPhotos.forEach((file) => {
    formData.append("preTreatmentPhotos", file);
  });

  return api.post("/treatment/create-update", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getTreatmentById = async (id: string) => {
  return api.get(`/treatment/getOne/${id}`);
};

export const getAllMedicalConditions = async () => {
  return api.get("/medical-condition/getAll");
};

export const getAllAllergies = async () => {
  return api.get("/allergy/getAll");
};
export const getAllHabits = async () => {
  return api.get("/habits/getAll");
};
// Pagination and Delete
export interface TreatmentPaginationPayload {
  curPage: number;
  perPage: number;
  sortBy: string;
  direction: "asc" | "desc" | string;
  whereClause: Array<{ key: string; value: string; operator: string }>;
}

export const paginateTreatments = async (
  payload: TreatmentPaginationPayload
) => {
  return api.post("/treatment/pagination", payload);
};

export const deleteTreatment = async (id: string) => {
  return api.get(`/treatment/delete/${id}`);
};

export const deletePreTreatmentPhoto = async (photoId: string) => {
  return api.get(`/treatment/pre-photo/delete/${photoId}`);
};

export interface TreatmentRecord {
  id: string;
  patientId?: string;
  patient?: { id: string; name: string };
  clinic?: { id: string; name: string };
  treatmentType?: string;
  treatmentObjective?: string;
}

export const getAllTreatments = async (): Promise<TreatmentRecord[]> => {
  const res = await api.get("/treatment/getAll");
  const list = res?.data?.data || res?.data || [];
  return Array.isArray(list) ? list : [];
};

// Treatment Result APIs
export const uploadMultipleTreatmentResults = async (
  treatmentId: string,
  beforeImages: File[] = [],
  afterImages: File[] = []
) => {
  const formData = new FormData();
  formData.append("treatmentId", treatmentId);

  // Only append files if they exist and are valid
  // Limit to 10 images per type to avoid database column size issues
  const limitedBeforeImages = beforeImages.slice(0, 10);
  const limitedAfterImages = afterImages.slice(0, 10);

  limitedBeforeImages.forEach((file) => {
    if (file && file instanceof File) {
      formData.append("beforeImages", file);
    }
  });

  limitedAfterImages.forEach((file) => {
    if (file && file instanceof File) {
      formData.append("afterImages", file);
    }
  });

  return api.post("/treatment/result/multiple", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export interface TreatmentResultOption {
  id: string;
  url: string;
}

export interface TreatmentResultOptions {
  treatmentId: string;
  beforeOptions: TreatmentResultOption[];
  afterOptions: TreatmentResultOption[];
}

export const getTreatmentResultOptions = async (
  treatmentId: string
): Promise<TreatmentResultOptions> => {
  const res = await api.get(`/treatment/result/options/${treatmentId}`);
  return res?.data?.data || res?.data || { treatmentId, beforeOptions: [], afterOptions: [] };
};

// Upload treatment results with image URLs (strings)
// Sends arrays of URL strings as expected by the API
export const uploadTreatmentResultsWithUrls = async (
  treatmentId: string,
  beforeImageUrls: string[] = [],
  afterImageUrls: string[] = []
) => {
  const formData = new FormData();
  formData.append("treatmentId", treatmentId);

  // Append each URL in the array - FormData will handle it as an array
  beforeImageUrls.forEach((url) => {
    formData.append("beforeImages", url);
  });

  afterImageUrls.forEach((url) => {
    formData.append("afterImages", url);
  });

  return api.post("/treatment/result/multiple", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};